// Lit ReactiveController that wires a component to ChatStateStore updates.
//
// Why this exists: @lit/context with `subscribe: true` re-renders consumers
// when the *provided value* (the store reference) changes — but our store
// reference is stable for the widget's lifetime. State mutations replace the
// internal `state` field but not the reference. So we need a separate
// subscription mechanism.
//
// This controller:
//   - subscribes to the store's listener list on hostConnected
//   - calls host.requestUpdate() on every notification → triggers re-render
//   - unsubscribes on hostDisconnected → no leaks if component is moved or
//     the widget is detached and re-attached
//
// Use it alongside @consume for state-aware components:
//
//   @consume({ context: chatStateContext })
//   @property({ attribute: false })
//   store!: ChatStateStore;
//
//   private _stateConsumer = new StateConsumerController(this, () => this.store);

import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ChatStateStore } from './chat-state-store.js';

export class StateConsumerController implements ReactiveController {
  private host: ReactiveControllerHost;
  private getStore: () => ChatStateStore | undefined;
  private unsubscribe: (() => void) | undefined;

  constructor(host: ReactiveControllerHost, getStore: () => ChatStateStore | undefined) {
    this.host = host;
    this.getStore = getStore;
    host.addController(this);
  }

  hostConnected(): void {
    this.trySubscribe();
  }

  /**
   * Lit calls hostUpdated after every render. Use this to retry subscription
   * if the store wasn't available at hostConnected time — e.g. when @consume
   * is wired but the provider attaches after the consumer's connectedCallback
   * (race condition in playgrounds, dynamic mounts, or anywhere the
   * provider isn't strictly an ancestor at parse time).
   *
   * In the production path (T12 root) the provider is attached before the
   * message-list connects, so trySubscribe in hostConnected succeeds and
   * this hook is a no-op. The cost is one extra reference check per render.
   */
  hostUpdated(): void {
    if (!this.unsubscribe) {
      this.trySubscribe();
    }
  }

  hostDisconnected(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
  }

  private trySubscribe(): void {
    if (this.unsubscribe) return; // already subscribed
    const store = this.getStore();
    if (!store) return;
    this.unsubscribe = store.subscribe(() => {
      this.host.requestUpdate();
    });
  }
}
